import { Injectable } from '@angular/core';
import { CVApiService } from 'src/app/reusables/services/cv-api.service';
import { Body, BudgetHistoryParams, BudgetNotification, BudgetNotificationResponse, BudgetObject, BudgetRecord, EmpGroup, GroupType, SpendingHistoryForBudgeting } from '../../models/budget.model';
import { ExportFileService } from 'src/app/gamification/command-center/services/export-file.service';
import { BehaviorSubject } from 'rxjs';
import { TypeFormats } from 'src/shared/types/exportItem';
import moment from 'moment';

@Injectable({
    providedIn: 'root'
})
export class BudgetService {
  private budgetListSubject = new BehaviorSubject<BudgetObject[]>([]);
  public budgetList$ = this.budgetListSubject.asObservable();
  isTabInitialLoadingSubject = false;

    constructor(
        private cvApiService: CVApiService,
        private exportFile: ExportFileService,
    ) { }

    async loadBudgets(): Promise<BudgetObject[]> {
        this.isTabInitialLoadingSubject = true;
        try {
            const url = "/api/v2/gamification/budget";
            const response = await this.cvApiService.get<BudgetObject[]>(url);
            if (response.status_code >= 200 && response.status_code < 300) {
                this.budgetListSubject.next(response.data);
                return response.data;
            } else {
                throw response;
            }
        } catch (error) {
            console.error("Failed getting data for budgets");
            throw error;

        } finally {
            this.isTabInitialLoadingSubject = false;
        }
    }

    async loadBudgetHistory({ budgetSettings, GroupID, GroupLevel }: BudgetHistoryParams): Promise<SpendingHistoryForBudgeting> {
        try {
            const url = `/api/v2/gamification/budget-history/groupId/${GroupID}/groupLevel/${GroupLevel}?interval=${budgetSettings.DateRange}&month=${budgetSettings.Month + 1}&year=${budgetSettings.Year}`;
            const response = await this.cvApiService.get<SpendingHistoryForBudgeting>(url);
            if (response.status_code >= 200 && response.status_code < 300) {
                return response.data;
            } else {
                throw response;
            }
        } catch (error) {
            console.error("Failed load budget history");
            throw error;
        }
    }

    async saveBudget(budgetList: BudgetObject[]): Promise<any> {
        const url = `/api/v2/gamification/budget`;
        const response = await this.cvApiService.post(url, budgetList);
        if (response.status_code >= 200 && response.status_code < 300) {
            return response;
        } else {
            throw response;
        }
    }

    async saveBudgetNotifications(budgetID: number, alertAt: number, data: BudgetNotification[]): Promise<any> {
        try {
            const url = `/api/v1/gamification/budget/${budgetID}/notifyLogins/${alertAt}`;
            const response = await this.cvApiService.post(url, JSON.stringify(data));
            if (response.status_code >= 200 && response.status_code < 300) {
                return response.data;
            } else {
                throw response;
            }
        } catch (error) {
            console.error("Failed saving budget notifications");
            throw error;
        }
    }


    async updateBudgetAlerts(budgetID: number, alertAt: number, data: BudgetNotification[], toggleNotifications?: boolean): Promise<any> {
        try {
            const url = `/api/v2/gamification/budget/${budgetID}/logins/${alertAt}?toggleNotifications=${toggleNotifications}`;
            const response = await this.cvApiService.post(url, data);
            if (response.status_code >= 200 && response.status_code < 300) {
                return response.data;
            } else {
                throw response;
            }
        } catch (error) {
            console.error("Failed saving budget notifications");
            throw error;
        }
    }

    async loadGroupTypes(): Promise<GroupType> {
        try {
            const url = `/api/v1/group_types`;
            const response = await this.cvApiService.get<GroupType>(url);
            if (response.status_code >= 200 && response.status_code < 300) {
                return response.data;
            } else {
                throw response;
            }
        } catch (error) {
            console.error("Failed getting data for group type");
            throw error;
        }
    }

    async loadGroupsByLevel(level: number): Promise<EmpGroup[]> {
        try {
            const url = `/api/v1/emp_groups/${level}`;
            const response = await this.cvApiService.get<EmpGroup[]>(url);
            if (response.status_code >= 200 && response.status_code < 300) {
                return response.data;
            } else {
                throw response;
            }
        } catch (error) {
            console.error("Failed getting data for emp groups by level");
            throw error;
        }
    }

    async setNotificationAlertAt(alertAtValue: number): Promise<any> {
        try {
            const url = `/api/v1/gamification/budget/0/logins/${alertAtValue}`;
            const response = await this.cvApiService.post(url, []);
            if (response.status_code >= 200 && response.status_code < 300) {
                return response.data;
            } else {
                throw response;
            }
        } catch (error) {
            console.error("Failed setting notification alert value");
            throw error;
        }
    }

    async loadBudgetNotificationLoginList(): Promise<BudgetNotificationResponse[]> {
        try {
            const url = '/api/v2/gamification/budget/notifyLogins';
            const response = await this.cvApiService.get<BudgetNotificationResponse[]>(url);
            if (response.status_code >= 200 && response.status_code < 300) {
                return response.data;
            } else {
                throw response;
            }
        } catch (error) {
            console.error("Failed to load budget notification login list");
            throw error;
        }
    }

    async setToggleNotificationStatus(budgetID: number, toggleValue: boolean): Promise<any> {
        try {
            const url = `/api/v2/gamification/budget/${budgetID}/logins/0?toggleNotifications=${toggleValue}`;
            const response = await this.cvApiService.post(url, []);
            if (response.status_code >= 200 && response.status_code < 300) {
                return response.data;
            } else {
                throw response;
            }
        } catch (error) {
            console.error("Failed to toggle notification status");
            throw error;
        }
    }

    //for export functions refer to ClearView Source\src\app\gamification\command-center\services\export-file.service.ts service.

    async exportBudgetHistory(body: Body) {
        this.exportFile.exportHistoryReport("/api/v1/gamification/budget-history-export", body);
    }

    async exportBudgetDefault(format: TypeFormats, body: Body, fileName: string) {
        const response = await this.exportFile.exportReport(`/api/v1/gamification/budget/default-export?format=${format}`, body);
        this.downloadFile(response, format, fileName);
    }

    private downloadFile(base64Data: string, format: TypeFormats, fileName: string) {
        const formatFile = format === 'excel' ? 'xlsx' : 'csv';
        const formatLabel = format === 'excel' ? 'Excel' : 'CSV';
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: this.getMimeType(formatFile) });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${fileName}${formatLabel}_${moment().format('DD MMM,YYYY h[h]mm[m]ss[s] A')}.${formatFile}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    }
    
    private getMimeType(format: 'xlsx' | 'csv'): string {
        switch (format) {
            case 'csv':
            return 'text/csv';
            case 'xlsx':
            return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
            default:
            return 'application/octet-stream';
        }
    }
}
